import {
  GatsbyImage,
  getImage,
  type GatsbyImageProps,
  type ImageDataLike,
} from 'gatsby-plugin-image';
import React, { type FC } from 'react';
import styled from '@emotion/styled';
import tokens from './tokens';
import { space } from '../../styles/tokens';

// GatsbyImage and HTMLImageElement onLoad types conflict and we don't use them anyway.
// We add back in a few ImgHTMLAttributes that GatsbyImageProps does not extend.
export interface ImageProps extends Omit<GatsbyImageProps, 'image' | 'onLoad'> {
  roundCorners?: 'all' | 'top' | 'left' | 'bottom' | 'right';
  src: string | ImageDataLike | null;
  srcSet?: string;
  width?: number;
  height?: number;
}

const borderRadiusOptions = {
  all: `${space[tokens.borderRadius]}`,
  top: `${space[tokens.borderRadius]} ${space[tokens.borderRadius]} 0 0`,
  left: `${space[tokens.borderRadius]} 0 0 ${space[tokens.borderRadius]}`,
  bottom: `0 0 ${space[tokens.borderRadius]} ${space[tokens.borderRadius]}`,
  right: `0 ${space[tokens.borderRadius]} ${space[tokens.borderRadius]} 0`,
} as const;

const StyledImageContainer = styled.div<{
  roundCorners: ImageProps['roundCorners'];
}>`
  height: 100%;

  ${({ roundCorners }) =>
    roundCorners &&
    `
    img {
      border-radius: ${borderRadiusOptions[roundCorners]};
    }
  `}
`;

export const Image: FC<ImageProps> = ({
  className,
  imgClassName,
  roundCorners,
  src,
  ...rest
}) => {
  if (typeof src === 'string') {
    return (
      <StyledImageContainer roundCorners={roundCorners} className={className}>
        <img src={src} className={imgClassName} {...rest} />
      </StyledImageContainer>
    );
  }

  const gatsbyImageData = getImage(src);

  if (gatsbyImageData === undefined) {
    return null;
  }

  return (
    <StyledImageContainer roundCorners={roundCorners}>
      <GatsbyImage
        className={className}
        imgClassName={imgClassName}
        image={gatsbyImageData}
        {...rest}
      />
    </StyledImageContainer>
  );
};

export default Image;
